import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { api } from '../../lib/api'
import { newAttemptNonce } from '../../lib/attempt'
import { seatOverride } from '../../lib/seat'
import { useSessionFeed } from '../../lib/useSessionFeed'
import { useLocale } from '../../lib/i18n'
import { notifyLine } from '../../lib/lineNotifications'
import type { MessageEvent as FeedMessage, Opening } from '../../lib/contract'

/**
 * How long the writing mark rests after a line lands, before it returns for the rest of the
 * gap. Short enough that a real pause still shows dots for most of its length; long enough
 * that a burst of quick lines does not strobe. ⚠ Not a typing model — that is phase 2 (a
 * per-character speed in the pack, deterministic and clamped). This constant knows nothing
 * about who is typing or how long the line is.
 */
const REST_AFTER_LINE_MS = 2000


/**
 * THE MESSAGING SURFACE'S SESSION — one thread, one being.
 *
 * The presence form (`PresencePrompt`) rendered as what it literally is: a
 * conversation in a messaging app. Per the mode ruling (Sanctum revival note §41,
 * 2026-08-19) the player is NOT in a room with anyone — they write, and someone
 * somewhere writes back. That is why this surface is cheap: the player is doing
 * the thing the fiction depicts, so nothing is translated
 * (`docs/specs/2026-08-19-the-two-axes.md` §2).
 *
 * Deliberately thinner than `useDeskSession`. The desk has instruments, a
 * dossier, an outbox, arrangements and an advance control. A thread has a thread.
 *
 * THREE LAWS THIS HOOK EXISTS TO KEEP:
 *
 *  1. **Nothing arrives unless the guest writes first.** The engine's own
 *     never-summons covenant (pack flag `openingWaitsForPlayer`), so this hook
 *     fires no continuation, no tick, and no advance. It sends what you type and
 *     listens. There is deliberately no `advance()` here.
 *  2. **The session must be kept awake while it is open.** `SurfacingService`
 *     freezes a session's clock when no SSE subscriber is attached and idles it
 *     after three minutes of silence — so a live thread pings `/activity`. Without
 *     it his lines generate and never surface, which reads as him ignoring you.
 *  3. **History has one source.** `/messages` is the only backlog path; the
 *     opening run's pre-traffic arrives through it like any other history
 *     (brief §6.4). The feed is for what happens next.
 */
export interface MessagingSession {
  status: 'idle' | 'starting' | 'live' | 'error'
  error: string | null
  sessionId: string | null

  /** Null once the run is done or the pack has none. Rendered by OpeningRun. */
  opening: Opening | null
  finishOpening: () => void

  /** The thread, oldest first. Both sides. */
  messages: FeedMessage[]
  /** He is composing — the engine is mid-generation for this turn. */
  awaiting: boolean

  /**
   * THE PICK-UP (2026-08-26, founder: "wait for him to pick up the phone").
   *
   * True only in the beat between a FRESH session opening and his first authored line being
   * shown. The join notice may stay up through it (the queue), and the writing mark carries the
   * rest (he is typing his hello). Never true on a revived thread — history is history and
   * paints whole.
   *
   * ⚠ This is not a fake delay dressed up. On a fresh open his burst lines are, in the fiction,
   * being written NOW — and a man does not type three lines in zero milliseconds. The pacer
   * already gives every LIVE turn its real typing time; this extends the same honesty to the
   * opening, which was the one place lines arrived instantly. The state is real (which line is
   * shown), read off the reveal, never off a timer pretending to be one.
   */
  connecting: boolean

  send: (text: string) => Promise<void>
  /**
   * THE TYPING SIGNAL (2026-09-22; docs/specs/2026-09-22-the-live-stretch-the-exact-change.md). Rides the
   * presence ping: true while the composer has text (the composer repeats it), false when it empties. The
   * engine holds her undelivered lines while it is true; the hold expires on its own. No call fires on it.
   */
  signalTyping: (typing: boolean) => void
}

/** How often to tell the server someone is still here. Comfortably under the 3-minute idle. */
const ACTIVITY_MS = 45_000

export function useMessagingSession(
  packIdProp?: string,
  opts: {
    /** Which person's thread. Undefined = the pack's home persona. */
    personaId?: string
    /** The pack-authored pick-up beat, ms (the door's `connectMs`). Null/undefined = default. */
    connectMs?: number | null
    /** Abandon any existing thread and open a new one. The reset control, and only it. */
    startFresh?: boolean
    /**
     * ⚠ THE GUEST HAS ALREADY WALKED THE RUN — the doorstep's *continue*, and only it.
     *
     * `OpeningGate` derives completion from ONE session's ledger, which was exactly right while a
     * pack had one thread and is wrong for a hall: home is Salvör, so a guest who spent a whole
     * visit with an advisor leaves her thread honestly un-played, and the letter comes back at
     * them on the next visit.
     *
     * Deriving it across sibling threads server-side is the principled long answer (Sanctum
     * addendum 20 §4: *has the guest written to anyone in the house*). This is better for the
     * case it covers, because it is not a derivation at all: **the guest said so.** Choosing
     * *continue* over *start again* is a positive statement that the run is behind them, made at
     * the one moment they were asked.
     */
    alreadyWalked?: boolean
  /**
   * ⚠ THE PRICE THE PLAYER AGREED TO, in micros (2026-08-27 — the wallet). Carried from the
   * threshold card straight to `startSession`, where the server checks it against today's plan
   * and refuses a mismatch rather than charging the new price. Never recomputed on the way down:
   * this is a promise the player made about a specific number.
   *
   * Undefined = no price applied (an unpriced pack, or a build with no wallet).
   */
    agreedPriceMicros?: number
    /**
     * LINE NOTIFICATIONS, LAYER 0 (2026-09-14). The person's name, as the title of a browser
     * notification carrying each live line of theirs while this tab is not being looked at.
     * Undefined = never notify. See `lib/lineNotifications.ts` for the rules.
     */
    notifyAs?: string
  } = {},
): MessagingSession {
  const { locale } = useLocale()
  const { packId: routePackId } = useParams()
  const packId = packIdProp ?? routePackId ?? ''
  const { personaId, connectMs = null, startFresh = false, alreadyWalked = false, agreedPriceMicros, notifyAs } = opts

  const [status, setStatus] = useState<MessagingSession['status']>('idle')
  const [error, setError] = useState<string | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [opening, setOpening] = useState<Opening | null>(null)
  const [messages, setMessages] = useState<FeedMessage[]>([])
  const [awaiting, setAwaiting] = useState(false)
  /*
   * THE STAGED ARRIVAL. `null` = no staging, every message shows (the only state a revived
   * thread ever has). A number = a fresh open is mid-arrival and only that many lines show.
   * Any real event — the guest sending, a live line landing on the feed — flushes to null:
   * the staging yields to anything that actually happens.
   */
  const [revealCount, setRevealCount] = useState<number | null>(null)
  /*
   * THE WRITING MARK ACROSS A PACED ARRIVAL (2026-08-31 — the typing indicator, re-admitted).
   *
   * ⚠ THE DEFECT THIS FIXES, found in live testing: the mark was cleared at
   * `chunk-gen-completed`, which fires when the MODEL is done — but the server then delivers
   * that batch one line at a time, paced 1:1 by the record's stamps, over the following ten
   * to sixty seconds. So the reader watched dots for the generation, then sat in silence
   * between lines with no sign that more was coming, and read a partial reply as a whole one.
   * (That is exactly how the founder found it: he screenshotted a four-message reply two
   * messages in.) `messaging-render-started` / `-completed` bracket the DELIVERY, which is
   * the interval a person is actually writing across, so the mark rides those instead.
   *
   * ⚠ IT MUST NEVER BE A LIE (the guard on the ruling that re-admitted this): `rendering` is
   * true only while lines that genuinely exist are queued to arrive. Never speculative, never
   * on an idle thread, never a claim that someone is about to write.
   *
   * ⚠⚠ INERT ON THE PERCEPTION LANE, AND THAT IS THE WHOLE OF WHAT IS LEFT TO DO (measured
   * 2026-08-31). `messaging-render-started/-completed` are published by `EngineCoordinator`
   * (the simulation-first lane). The teachers, Sokratis and Ivor run on
   * `PerceptionFirstCoordinator`, which publishes only `chunk-gen-started/-completed` — and
   * completed fires when the MODEL is done, before the paced delivery begins. Traced live:
   * dots off at 11.2s, then lines landing at 12.4s, 20.4s and 28.4s with no mark at all.
   * So on that lane the client cannot know more lines are coming, and inventing dots would
   * break the guard above. **What is owed is one engine addition: a bracket around the paced
   * emission** (or a more-coming flag per line) — founder's lane. This code is the client
   * half, correct and waiting for it.
   * ⚠ NOT the seam: `MessageEvent.RevealDurationMs`. That belongs to
   * `doctrine-spoken-word-has-duration.md` §6.1 — word-by-word reveal of SPOKEN lines — and
   * the doctrine specifies 0 for typed messages, which is why it reads 0 here. Correct as is.
   */
  const [rendering, setRendering] = useState(false)
  /*
   * THE TAIL OF THE GAP. Dots held solidly for thirty seconds say "she has been typing for
   * thirty seconds", which is not what waiting for a person looks like. So each arrival buys
   * a beat of silence — she sent that one, and has not started the next — after which the
   * mark returns for the remainder of the gap. Silence, then dots, then the line: the shape a
   * real thread has. If the next line lands inside the beat the mark simply never blinks,
   * which is correct — that WAS one quick burst.
   */
  const [restingUntil, setRestingUntil] = useState(0)
  const [, forceTick] = useState(0)

  // React 18 StrictMode double-invokes effects in dev; without this the surface
  // would open two sessions and pay for both. Keyed by the thread being opened, so
  // walking from one door to another still opens the second one.
  const startGuard = useRef<string | null>(null)

  /*
   * THE PURCHASE KEY FOR THIS ATTEMPT. What it is and what it fixes: `lib/attempt.ts`. It used to
   * be `${packId}:${personaId}:${startFresh}` — `indridi::true`, on every *start again* forever.
   *
   * ⚠ MINTED WHERE `startGuard` IS SET, and that is the whole of the correctness argument here:
   * the guard above already defines "one attempt", so the key and the guard cannot drift apart.
   *
   *   · StrictMode's second invoke returns early at the guard, so it never mints a second nonce →
   *     one sitting, which is the property the old constant key was accidentally providing.
   *   · *Start again* remounts (`PackSurface` renders the surface under `key={generation}`), so a
   *     fresh ref mints a fresh nonce → a genuinely new sitting.
   *   · Walking door A → B → A mints again on the third leg, which is right: it only reaches
   *     `startSession` at all when resuming A failed, and that IS a new sitting.
   *
   * A failed start leaves its hold orphaned rather than reused — the designed outcome, since a hold
   * expires and returns (`expiresAt`; `the-wallet.md` §4).
   */
  const attemptId = useRef<string>('')

  /*
   * ⚠ THE RUN IS WALKED ONCE A VISIT (bug, 2026-08-23: "open a conversation and the onboarding
   * screens open again").
   *
   * `OpeningGate.Complete` is derived from the ledger — a player line at or after the pack's
   * starting time — which is exactly right for the session it is asked about and blind to the
   * one thing the hall introduced: THE GUEST WALKS THE RUN AND THEN GOES SOMEWHERE ELSE. They
   * have not written to Bergur, so his thread is honestly incomplete; they have still read
   * Hildur's letter, and being handed it again on the way back to his door is the surface
   * forgetting the last thing that happened.
   *
   * The rooms of the hall were never affected — `PackDefinition.ForPersona` gives a non-home
   * persona a card-less opening, so the run cannot follow you into a room. Only home could
   * re-show it, and only until the first line is written.
   *
   * A ref, not state: nothing renders from it, and it must be readable by the fetch that is
   * already in flight when the reader taps the last card.
   *
   * Deliberately NOT a way of remembering the walk between visits — a fresh mount starts false,
   * so a guest who read the letter, closed the tab and never wrote a word to anyone still meets
   * it next time. That is the honest reading of "never got in", and moving it would mean storing
   * completion rather than deriving it.
   */
  const walked = useRef(false)
  // The doorstep's answer arrives before the first load, so it is read the same way the ref is.
  if (alreadyWalked) walked.current = true

  useEffect(() => {
    const key = `${packId}::${personaId ?? ''}`
    if (!packId || startGuard.current === key) return
    startGuard.current = key
    // One attempt, one purchase key. See `attemptId` for why it is minted exactly here.
    attemptId.current = `${key}:${newAttemptNonce()}`

    // Walking to another door: clear the previous person's thread before the next one
    // loads, so his last line is never briefly visible under her name.
    setSessionId(null)
    setMessages([])
    setOpening(null)
    /*
     * ⚠ AND THE RESTING MARK, which was left behind (2026-08-23, found in the trace that fixed the
     * attribution bug). `awaiting` is set by the feed's chunk-gen-started and cleared by its
     * completion — so leaving one thread mid-generation carried the mark into the next, where it
     * says *this person is writing to you* about someone who is not. On the starved surface that
     * is the one signal the thread is allowed to give, which makes a false one worse here than it
     * would be anywhere else.
     */
    setAwaiting(false)

    /*
     * ⚠ A SUPERSEDED LOAD MUST NOT WRITE (bug, 2026-08-23: "Salvör's opening dialogue seems to be
     * attributed to Una").
     *
     * Opening a thread is four awaited round trips, and the guest can change threads inside them —
     * which the POC does BY DESIGN on the very first visit: the cards live on home's opening (a
     * non-home persona gets a card-less one), so the surface opens home, and the reception then
     * hands the guest to Una. Two chains were in flight and the LAST ONE TO FINISH won, so the
     * family's seeded burst arrived under a title strip reading Una.
     *
     * `startGuard.current` already holds the key of the thread the surface actually wants, so it
     * is also the liveness test: anything that started under a different key is stale and must
     * commit nothing. Guarding at the commits rather than cancelling the fetches is deliberate —
     * the requests are harmless and often warm the very session the guest walks into next; only
     * the writes are dangerous.
     */
    const stale = () => startGuard.current !== key

    void (async () => {
      setStatus('starting')
      try {
        /*
         * ONE PERSON, ONE THREAD, FOREVER (2026-08-22 — the hall, addendum 10 item 1; the
         * presence format's "one eternal session, revived per visit").
         *
         * ⚠ THIS HOOK USED TO START A NEW SESSION ON EVERY MOUNT — every reload was a stranger
         * knocking for the first time, and the format doc's claim that the session is eternal
         * was true only of the engine, never of the client. It went unnoticed because a bench
         * pack is played in one sitting; it would not have survived a second visit, and the
         * corridor's own acceptance criterion is "opening a door twice in a year lands in the
         * same talk".
         *
         * So: look for this guest's thread with this person first, and revive it. Start only
         * when there is nothing to come back to.
         */
        /*
         * ⚠ RESUMING IS AN OPTIMISATION; FAILING TO RESUME MUST DEGRADE TO STARTING, NEVER TO
         * NOTHING (2026-08-22, found by looking at a dead surface).
         *
         * This block used not to exist, so a session opened unconditionally. When resume was
         * added, `/threads` became a hard dependency of having a conversation at all — and the
         * first time it 404'd (an API a build behind) the guest got a blank sheet with the word
         * "Not Found" over it, which is the worst thing a surface can do: fail at the one moment
         * it is meant to be a person answering.
         *
         * So the lookup is its own try. Anything at all going wrong here — an old server, a
         * malformed pack, a transient error — costs the reader their history and nothing else.
         */
        let id: string | null = null
        if (!startFresh) {
          try {
            const { doors } = await api.getThreads(packId)
            const door = personaId
              ? doors.find((d) => d.personaId === personaId)
              : doors.find((d) => d.place === 'home') ?? doors[0]
            if (door?.sessionId) {
              // `live` means the server already holds it in memory; reviving anyway is harmless
              // but costs a disk read and a rebuild of the graph.
              if (!door.live) await api.revive(door.sessionId)
              id = door.sessionId
            }
          } catch {
            // Deliberately silent to the reader: they asked for a conversation, not a report on
            // our storage layer. A fresh thread is a worse outcome than a resumed one and a far
            // better one than an error message.
          }
        }

        let openedFresh = false
        if (!id) {
          openedFresh = true
          const started = await api.startSession({
            packId,
            personaId,
            // The player is themselves; this names the seat, not a character to
            // impersonate. The zero-biography rule is the whole point — everything
            // he learns about who you are has to come from you
            // (`2026-08-19-the-two-axes.md` §3.1).
            playerCharacterContent: 'the guest',
            strategy: 'PerceptionFirst',
            // THE LOCALE ROUND-TRIP (language design §5, built 2026-08-20). The server has
            // carried `Session.Language` since Phase 1 landed in August; no client ever set it,
            // so every session ran in the server default and an Icelandic reader was answered
            // in English. Fixed at creation and never after: the language of a conversation is
            // not a display setting, and switching it mid-thread would leave half a history in
            // one language and half in another.
            language: locale,
            agreedPriceMicros,
            requestId: attemptId.current,
            // The player's clock, for a pack that runs on it (2026-09-11).
            utcOffsetMinutes: -new Date().getTimezoneOffset(),
            // The admin seat override, or nothing at all. See `lib/seat.ts`.
            ...seatOverride(),
          })
          id = started.sessionId
        }

        if (stale()) return
        setSessionId(id)

        // Opening first, then history: the run is what the player meets, and the
        // pre-traffic behind it should already be in place when they finish it.
        // `complete` is the server's own skip rule, so a revived thread walks past it.
        const run = await api.getOpening(id)
        if (stale()) return
        if (run.opening && run.opening.cards.length > 0 && !run.complete && !walked.current)
          setOpening(run.opening)

        const history = await api.getMessages(id)
        if (stale()) return
        setMessages(history.messages)

        /*
         * THE PICK-UP (see `connecting` above). Only a session THIS mount created, whose whole
         * history is his side (the authored burst — the guest has not written a word), stages
         * its arrival: a beat of nothing (the queue; the join notice holds through it), then his
         * lines at typing pace. The gaps follow line length the way a person typing does. A
         * revived thread never enters this branch, whatever it contains — replaying history
         * slowly would be the surface performing, which is the thing it must never do.
         */
        const lines = history.messages
        if (
          openedFresh &&
          lines.length > 0 &&
          lines.length <= 6 &&
          lines.every((m) => m.senderKind !== 'Player')
        ) {
          setRevealCount(0)
          // The pick-up is the pack's to set (a marketplace switches you through briskly; a
          // man at a bench sets the cane down first); the typing gaps follow line length.
          const gap = (i: number) =>
            i === 0 ? (connectMs ?? 1800) : 1200 + Math.min(lines[i - 1].content.length * 30, 1600)
          let shown = 0
          const step = () => {
            if (stale()) return
            shown += 1
            // Functional update so a flush (null) that raced in stays a flush.
            setRevealCount((c) => (c === null ? null : shown >= lines.length ? null : shown))
            if (shown < lines.length) window.setTimeout(step, gap(shown))
          }
          window.setTimeout(step, gap(0))
        }
        setStatus('live')
      } catch (e) {
        // A thread the guest has already left may fail for any reason; saying so would put an
        // error over a conversation that is loading perfectly well.
        if (stale()) return
        setError(e instanceof Error ? e.message : String(e))
        setStatus('error')
      }
    })()
  }, [packId, personaId, startFresh])

  useSessionFeed(sessionId, {
    message: (m) => {
      // A live line outranks the staged arrival — anything real flushes it.
      setRevealCount(null)
      // She has just sent one; she has not started the next. The mark rests, then returns.
      setRestingUntil(Date.now() + REST_AFTER_LINE_MS)
      // A live line of theirs, and only a live one: the hydrate above never reaches here, so a
      // backlog on load cannot fire a stack of notifications.
      if (notifyAs && m.senderKind !== 'Player') notifyLine(notifyAs, m.content, m.messageId)
      setMessages((prev) =>
        // The feed can repeat a line the hydrate already carried; the id is the
        // only thing that settles it.
        prev.some((p) => p.messageId === m.messageId) ? prev : [...prev, m],
      )
    },
    'chunk-gen-started': () => setAwaiting(true),
    'chunk-gen-completed': () => setAwaiting(false),
    // The delivery bracket, not the generation bracket — see `rendering` above.
    'messaging-render-started': () => setRendering(true),
    'messaging-render-completed': () => {
      setRendering(false)
      setRestingUntil(0)
    },
    error: (e) => setError(e.message),
  })

  // Law 2 — keep the clock running while the thread is open.
  useEffect(() => {
    if (!sessionId) return
    const tick = () => void api.activity(sessionId).catch(() => {})
    tick()
    const h = window.setInterval(tick, ACTIVITY_MS)
    return () => window.clearInterval(h)
  }, [sessionId])

  const send = useCallback(
    async (text: string) => {
      const body = text.trim()
      if (!sessionId || !body) return
      // Writing over the staged arrival flushes it — the guest outranks the staging.
      setRevealCount(null)
      setAwaiting(true)
      try {
        await api.sendInput(sessionId, body)
      } catch (e) {
        setAwaiting(false)
        setError(e instanceof Error ? e.message : String(e))
      }
      // The sent line is NOT appended here — the server emits it on the feed like
      // any other message, so the thread has one writer and cannot drift from the
      // record. An optimistic echo would be a second source of truth.
    },
    [sessionId],
  )

  const signalTyping = useCallback(
    (typing: boolean) => {
      if (!sessionId) return
      void api.activity(sessionId, { typing }).catch(() => {})
    },
    [sessionId],
  )

  const finishOpening = useCallback(() => {
    walked.current = true
    setOpening(null)
  }, [])

  /*
   * The staged arrival, applied at the seam: internal state always holds the whole thread
   * (one source of truth); only what is HANDED OUT is staged. The writing mark rides the
   * staging too — between reveals he is, in the fiction, typing the next line.
   */
  const staged = revealCount !== null
  const shownMessages = staged ? messages.slice(0, revealCount) : messages

  /*
   * The rest is a moment in time, not a state, so nothing re-renders when it lapses — this
   * wakes the surface once, exactly when the mark is due back. Cleared on every change, so a
   * line landing inside the beat simply reschedules it.
   */
  useEffect(() => {
    if (!rendering || restingUntil === 0) return
    const left = restingUntil - Date.now()
    if (left <= 0) return
    const h = window.setTimeout(() => forceTick((n) => n + 1), left)
    return () => window.clearTimeout(h)
  }, [rendering, restingUntil])

  // The mark: a batch is in flight, and she is not inside the beat after sending a line.
  const writingMark = rendering && Date.now() >= restingUntil

  return {
    status,
    error,
    sessionId,
    opening,
    finishOpening,
    messages: shownMessages,
    // The whole staging shows the writing mark — the pick-up beat included, so the wait is
    // never a blank screen: the dots ARE the phone being picked up. `writingMark` extends the
    // same honesty across a batch's paced delivery (see `rendering`).
    awaiting: awaiting || staged || writingMark,
    connecting: staged && revealCount === 0,
    send,
    signalTyping,
  }
}
